import { BillingAttachment } from "@wingspanhq/wingspan-api-sdk";

type Params = {
  /** Attachments currently bound to the invoice/payable on v3. */
  current: BillingAttachment[];
  /** Member-file ids the form wants bound, in form order. */
  desiredMemberFileIds: string[];
};

type Result = {
  /** Member-file ids to bind, in form order. */
  attachMemberFileIds: string[];
  /** Ids of the bound attachments to detach. */
  detachAttachmentIds: string[];
};

/**
 * Reconciles the attachments a form submitted against what is bound on v3.
 *
 * v3 has no bulk attachment write — binding and detaching are per-file verbs —
 * so an edit becomes a set difference keyed on the member-file id. Comparing
 * against the current bound set (rather than against what the form was seeded
 * with) keeps a re-submit idempotent: a file already bound is not bound twice,
 * and a file already gone is not detached again.
 *
 * Attachments with no id or no member-file id are ignored — neither verb can
 * address them.
 */
export const selectorBillingAttachmentSync = (params: Params): Result => {
  const boundMemberFileIds = params.current
    .map(attachment => attachment.memberFileId)
    .filter((memberFileId): memberFileId is string => !!memberFileId);

  return {
    attachMemberFileIds: params.desiredMemberFileIds.filter(
      memberFileId => !boundMemberFileIds.includes(memberFileId)
    ),
    detachAttachmentIds: params.current
      .filter(
        attachment =>
          !!attachment.id &&
          !!attachment.memberFileId &&
          !params.desiredMemberFileIds.includes(attachment.memberFileId)
      )
      .map(attachment => attachment.id!)
  };
};
